import type { EquipmentItem } from '@sb/integrations/types';
import {
  getTooltipOptions,
  isEndEffector,
  optionBelongsToKind,
} from '@sb/integrations/utils';
import type { TCPOffsetOption } from '@sb/motion-planning';
import { DEFAULT_TCP_OFFSET_OPTION } from '@sb/motion-planning';
import { WaypointStep } from '@sb/remote-control/step';
import type { Space } from '@sb/routine-runner';

import { pinsChildTooltip } from '../step-rules/weld';

import { ActionRequiredError, type WarningFinding } from './errors';

/** As much of a taught pose as judging its tooltip needs. */
type PoseWithTooltip = Pick<Space.Position, 'tcpOption' | 'gripperID'>;

/** One motion target, in the shape a tooltip rule reads it. */
export interface MotionTargetReference {
  targetKind: string | undefined;
  target: PoseWithTooltip | null | undefined;
  positionListID: string | null | undefined;
  /** Absent means the pass cycles the list, landing on a different row each time. */
  positionListIndex: number | null | undefined;
  /**
   * Which pose a pallet waypoint resolves to. Absent on every other target, and
   * on an arc, which carries no pallet configuration of its own.
   */
  palletMoveType?: 'box' | 'approach' | 'pickup';
  /**
   * Whether the target commands joint angles rather than a pose, as the runner
   * receives it. A joint-matched target needs no tooltip, so it cannot be
   * missing one.
   */
  matchesJointAngles?: boolean;
}

/**
 * Whether the enclosing step pins the tooltip for every child it holds: a Weld
 * step with its torch, or a constant-speed Move step. The runner answers to that
 * frame before it reads a child's own selection, so a child's `tcpOption` names
 * nothing there and no rule may block on it.
 */
export function parentPinsTooltip({
  parents,
  routine,
}: {
  parents: Array<{ id: string; stepKind: string }>;
  routine: {
    stepConfigurations: Record<string, { args?: unknown } | undefined>;
  };
}): boolean {
  const parent = parents[0];

  if (parent == null) return false;

  if (pinsChildTooltip(parent.stepKind)) return true;

  if (parent.stepKind !== 'MoveArmToV2') return false;

  const parentArgs = routine.stepConfigurations[parent.id]?.args as
    | { enforceConstantSpeed?: boolean }
    | undefined;

  return parentArgs?.enforceConstantSpeed === true;
}

/**
 * Whether a space item's poses are computed rather than stored. Both pallet
 * kinds declare `positions` as an always-empty array and build their real pick
 * points at run time from the layout, so nothing here can read a tooltip off
 * them.
 */
function isGeneratedPositionItem(item: Space.Item | undefined): boolean {
  return item?.kind === 'palletBoxes' || item?.kind === 'palletBase';
}

/**
 * The space item a target reads its poses from, or `undefined` when the target
 * carries its own pose. One lookup, so the tooltip rules cannot disagree about
 * which item a target names.
 *
 * Which item is decided by the id the runner will actually be sent, not by
 * `targetKind` alone: a `'relative'` target carries its list id through as
 * well, and reading only `'positionList'` would leave every relative-to-a-list
 * target answering for a pose it does not use.
 */
function itemBehindTarget({
  targetKind,
  positionListID,
  spaceItems,
}: Pick<MotionTargetReference, 'targetKind' | 'positionListID'> & {
  spaceItems: Space.Item[];
}): Space.Item | undefined {
  const emittedID = WaypointStep.emittedPositionListID({
    targetKind: targetKind as Parameters<
      typeof WaypointStep.emittedPositionListID
    >[0]['targetKind'],
    positionListID: positionListID ?? null,
  });

  return emittedID == null
    ? undefined
    : spaceItems.find((space) => space.id === emittedID);
}

/**
 * Why a target cannot say which tooltip it was taught with. `undefined` means
 * it can.
 *
 * - `palletComputed` — a pallet builds the pose from its layout when the
 *   routine runs and records no tooltip on it.
 * - `visionGenerated` — a vision position list is filled by a Locate at run
 *   time. Its rows are replaced on every save, so the editor never sees one to
 *   read a tooltip off.
 * - `poseRecordsNone` — the pose is stored, and stores no tooltip it can be
 *   resolved through: either none at all, or a pre-2024 bare numeric, which
 *   names a position rather than a device.
 * - `poseTooltipUnsupplied` — nothing on this robot answers the tooltip the
 *   pose names: no fitted equipment publishes it, the row that did is switched
 *   off, or a camera's tool-frame calibration has gone.
 */
export type NoSavedTooltipReason =
  | 'palletComputed'
  | 'visionGenerated'
  | 'poseRecordsNone'
  | 'poseTooltipUnsupplied';

/**
 * Whether this robot's equipment still supplies the tooltip a step names.
 *
 * The editor's counterpart to botman's Active Tooltip reconciler, and
 * deliberately the same two questions in the same order, so a reference the
 * runtime would revert is one the editor already flagged:
 *
 * - A reference carrying a `gripperID` names one exact row, so that row must
 *   exist AND still offer the option. A gripper whose config kept the row but
 *   dropped this one tooltip is the case an existence check alone misses.
 * - A reference carrying only an option name names a kind, not a row, so some
 *   row of that kind must exist. A legacy numeric (`ee-0`) embeds no kind and
 *   so is supplied by nothing.
 *
 * Enabled or disabled, in both branches: existence is the test. Disabling a
 * gripper never moves the Active Tooltip, and it must not light up every
 * routine that names its tooltip either.
 *
 * `wrist` is the flange, which is not equipment. `wristCamera` is exempt from
 * the offering check for the reason the reconciler exempts it: the enumeration
 * drops a camera the moment its tool-frame calibration goes away, so judging
 * that here would flag a reference that calibrating — not re-authoring — puts
 * right. The exemption is scoped exactly as the reconciler scopes it: a
 * reference naming a specific camera row still requires that row to exist —
 * the reconciler reverts a camera tooltip whose row was deleted outright.
 */
export function isTooltipSupplied({
  tcpOption,
  gripperID,
  equipment,
}: {
  tcpOption: TCPOffsetOption;
  gripperID?: string;
  equipment: EquipmentItem[];
}): boolean {
  if (tcpOption === DEFAULT_TCP_OFFSET_OPTION) {
    return true;
  }

  if (gripperID != null) {
    const owner = equipment.find((item) => item.id === gripperID);

    if (owner == null) return false;

    if (tcpOption === 'wristCamera') return true;

    return getTooltipOptions(owner.config).some(
      (option) => option.tcpOption === tcpOption,
    );
  }

  if (tcpOption === 'wristCamera') return true;

  return equipment.some((item) =>
    optionBelongsToKind(tcpOption, item.config.kind),
  );
}

/**
 * The pre-2024 spelling of a gripper tooltip, a bare index with no kind in it.
 * No naming scheme matches one, so it identifies no device — but the offset
 * walk does not match by name: a fitted gripper answers any `ee-*` option with
 * its own transform, so the pose plays at whatever tip the robot happens to
 * carry. It is reported as `poseRecordsNone` because that is the operator's
 * question and the operator's fix: the pose records nothing anybody can aim.
 */
function namesNoKind(tcpOption: TCPOffsetOption): boolean {
  return /^ee-\d+$/.test(tcpOption);
}

/**
 * Whether a pallet waypoint resolves to a pose somebody taught. A `'pickup'`
 * move answers with the box type's own `pickupPosition`, which is taught and
 * carries its tooltip, and which the runner throws without — so it is never the
 * empty case this rule reports. Every other move type is computed from the
 * layout.
 */
function palletMoveReadsATaughtPose(
  palletMoveType: MotionTargetReference['palletMoveType'],
): boolean {
  return palletMoveType === 'pickup';
}

/**
 * Every pose a motion target can resolve to, for reading tooltips off.
 *
 * A target that cycles a position list contributes all of its rows, because the
 * row a pass lands on is chosen at run time — the editor cannot know which one,
 * so it has to answer for all of them. A target that pins an index resolves to
 * that row alone (`index = positionListIndex ?? parentCompletedCount` in the
 * runner), so the rows it never visits say nothing about it. An expression
 * target resolves to nothing knowable here and so contributes no poses.
 */
export function posesBehindTarget({
  targetKind,
  target,
  positionListID,
  positionListIndex,
  spaceItems,
}: MotionTargetReference & {
  spaceItems: Space.Item[];
}): PoseWithTooltip[] {
  if (targetKind === 'expression') return [];

  const emittedID = WaypointStep.emittedPositionListID({
    targetKind: targetKind as Parameters<
      typeof WaypointStep.emittedPositionListID
    >[0]['targetKind'],
    positionListID: positionListID ?? null,
  });

  if (emittedID != null) {
    const item = spaceItems.find((space) => space.id === emittedID);

    const positions = ((item as { positions?: PoseWithTooltip[] })?.positions ??
      []) as PoseWithTooltip[];

    // A pallet builds its poses from the layout rather than indexing that
    // array, so an index there names no particular row.
    if (positionListIndex == null || isGeneratedPositionItem(item)) {
      return positions;
    }

    const pinned = positions[positionListIndex];

    // An index past the end names no pose. The out-of-bounds rule reports that
    // on its own field; answering here too would blame the tooltip for it.
    return pinned ? [pinned] : [];
  }

  return target ? [target] : [];
}

/**
 * Whether an `'auto'` selection can be honored: every pose it may resolve
 * to has to record the tooltip it was taught with.
 *
 * Poses taught before the capture existed record none, and so do poses that
 * vision or the agent generated. Nothing is knowable about an empty set, so it
 * passes rather than accusing a target that names no pose yet.
 */
export function everyPoseRecordsATooltip(poses: PoseWithTooltip[]): boolean {
  return poses.every((pose) => pose.tcpOption != null);
}

/** The locate skill's stand-in for "whichever gripper is fitted". */
const WILDCARD_TOOLTIP = 'ee-any';

/**
 * Whether this robot still places a saved pose at the tooltip it was taught
 * with.
 *
 * Deliberately not {@link isTooltipSupplied}, which tracks the Active Tooltip
 * reconciler and answers existence enabled or not — disabling a gripper must
 * not light up every routine that names its tooltip. This one tracks the
 * OFFSET WALK, which is what places the arm, and which is stricter on one
 * branch.
 *
 * The question is what a row OFFERS, not what its kind could offer:
 * `getTooltipOptions` drops an emptied custom-gripper slot and an uncalibrated
 * camera, and each of those is a frame the walk has no transform for. A kind
 * check would pass them and the arm would move somewhere else — the flange for
 * the gripper, the other slot's tip for a dual changer.
 *
 * The set it reads is the one the walk composes, `chain.links` plus a gripper
 * named by id. `owner()` reads `chain.links` too, so the two agree today — but
 * the walk is the authority here, because `owner()` does not decide a gripper
 * tooltip's placement at all. Follow the walk if they ever diverge.
 *
 * - An id naming an ACTUATING GRIPPER row settles it outright, disabled rows
 *   included: the walk stands such a row in for the one the chain is missing,
 *   and skips every rival gripper to do it. So that row offering the option is
 *   the answer, and its NOT offering it is a failure rather than a reason to
 *   look further — the walk will not look further either.
 * - An id naming anything else — a sensor, or nothing at all — stands in for
 *   nothing, and falls through to the name check below.
 * - Otherwise the option is matched against ENABLED rows alone. A pose taught
 *   while a gripper was enabled and replayed after its row is switched off no
 *   longer composes that gripper: it plays at another enabled gripper's tip, or
 *   at the flange when there is none.
 */
function chainResolvesSavedTooltip({
  tcpOption,
  gripperID,
  equipment,
}: {
  tcpOption: TCPOffsetOption;
  gripperID?: string;
  equipment: EquipmentItem[];
}): boolean {
  if (tcpOption === DEFAULT_TCP_OFFSET_OPTION) return true;

  // `'ee-any'` is a wildcard for whichever gripper is fitted, not a frame a
  // pose recorded, so it resolves on every robot and there is no equipment
  // state that makes it missing. Nothing in `getTooltipOptions` publishes it,
  // so without this every skill capture pose would report forever.
  // `TargetSettingsHelpText` carves it out on the same grounds; the two sides
  // agree deliberately, so a change to one belongs in the other.
  if (tcpOption === WILDCARD_TOOLTIP) return true;

  const offersIt = (item: EquipmentItem) =>
    getTooltipOptions(item.config).some(
      (option) => option.tcpOption === tcpOption,
    );

  // Only an actuating gripper stands in for the row the chain is missing. A
  // pose naming a non-gripper row — a force/torque sensor's own face, say —
  // contributes nothing once that row is switched off, and the walk resolves
  // it by option name like any other, so the id buys it nothing here either.
  //
  // Once the id does name a gripper, that row ANSWERS: whether it offers the
  // option is the verdict, not one more reason to look elsewhere. The walk
  // commits the same way — `isReplacedBySpecifiedGripper` skips every rival
  // gripper on the strength of the named row's kind alone — so falling through
  // here would clear a pose the robot then plays at the wrong tool.
  if (gripperID != null) {
    const owner = equipment.find((item) => item.id === gripperID);

    if (owner && isEndEffector(owner.config.kind)) {
      return offersIt(owner);
    }
  }

  return equipment.some((item) => item.isEnabled && offersIt(item));
}

/**
 * Why this target cannot answer "which tooltip was this taught with?", or
 * `undefined` when it can.
 *
 * An expression target is a pass-through to the position list it names, so it
 * answers with that list's rows rather than with a shape of its own. Nothing
 * here can extract the id, so it is left to the rules that judge the list.
 */
export function noSavedTooltipReason({
  targetKind,
  target,
  positionListID,
  positionListIndex,
  palletMoveType,
  matchesJointAngles,
  spaceItems,
  equipment,
}: MotionTargetReference & {
  spaceItems: Space.Item[];
  /**
   * This robot's equipment. Absent means the caller supplied none, so which
   * tooltips exist is unknown and the supplied check is skipped rather than
   * accusing every pose at once.
   */
  equipment: EquipmentItem[] | undefined;
}): NoSavedTooltipReason | undefined {
  // A joint-matched target commands stored joint angles, and the runner never
  // resolves a tooltip for one. The exception is the dynamic-base pass, which
  // re-runs the target with pose generation forced; the caller reports that as
  // not joint-matched, because for tooltip purposes it is not.
  if (matchesJointAngles) return undefined;

  if (targetKind === 'expression') return undefined;

  const item = itemBehindTarget({ targetKind, positionListID, spaceItems });

  if (isGeneratedPositionItem(item)) {
    return palletMoveReadsATaughtPose(palletMoveType)
      ? undefined
      : 'palletComputed';
  }

  if (item?.kind === 'visionPositionList') return 'visionGenerated';

  const poses = posesBehindTarget({
    targetKind,
    target,
    positionListID,
    positionListIndex,
    spaceItems,
  });

  if (!everyPoseRecordsATooltip(poses)) return 'poseRecordsNone';

  // A bare numeric is a recorded tooltip only in spelling. Judged before the
  // equipment check because no fitted equipment settles it either way: whatever
  // gripper is on the robot answers the name, so the pose plays at a tip nobody
  // chose rather than failing loudly.
  if (
    poses.some((pose) => pose.tcpOption != null && namesNoKind(pose.tcpOption))
  ) {
    return 'poseRecordsNone';
  }

  // Matched by kind alone, with no `gripperID`: the question is whether this
  // robot carries the KIND of tool the position was taught with. A pose taught
  // on a 2FG7 replayed against a 3FG15 asks for a frame no fitted device
  // publishes, and every gripper answers an `ee-*` name it does not own, so
  // without this the arm silently moves the wrong tool's tip.
  if (equipment == null) return undefined;

  const unsupplied = poses.some(
    (pose) =>
      pose.tcpOption != null &&
      !chainResolvesSavedTooltip({
        tcpOption: pose.tcpOption,
        gripperID: pose.gripperID,
        equipment,
      }),
  );

  return unsupplied ? 'poseTooltipUnsupplied' : undefined;
}

/**
 * The reasons no equipment change can answer. A pallet and a vision list build
 * their poses when the routine runs, and a pose that recorded no tooltip never
 * had one to record — including the pre-2024 bare numeric, which names a
 * position rather than a device. Fitting, enabling or calibrating anything
 * leaves all three exactly as they were, so the point has to be taught again or
 * the step pointed at a different tooltip.
 *
 * The one reason absent is the reversible one: a tooltip nothing currently
 * offers can be offered again.
 */
const NO_EQUIPMENT_CHANGE_FIXES_IT = new Set<NoSavedTooltipReason>([
  'palletComputed',
  'visionGenerated',
  'poseRecordsNone',
]);

/** The operator's sentence for each reason. */
const NO_SAVED_TOOLTIP_MESSAGE: Record<
  NoSavedTooltipReason,
  (stepNoun: string) => string
> = {
  palletComputed: (stepNoun) =>
    `A pallet has no saved tooltip. Choose a different tooltip for this ${stepNoun}.`,
  visionGenerated: (stepNoun) =>
    `A vision position list has no saved tooltip. Choose a different tooltip for this ${stepNoun}.`,
  // The only one of the three refusals whose target can be re-taught, which is
  // the likelier fix: a re-taught position records the tooltip it was taught
  // with, so "Saved tooltip" resolves afterwards. True of both cases this
  // reason covers, the pose that recorded nothing and the bare numeric.
  poseRecordsNone: (stepNoun) =>
    `This position has no saved tooltip. Choose a different tooltip for this ${stepNoun} or reteach the position.`,
  // No instruction, and so no `stepNoun`: the likely remedies are enabling the
  // equipment or changing the mode, neither of which the step offers, so
  // pointing at the tooltip picker would name the least likely fix. The three
  // that refuse keep their instruction — they stop the routine, so the operator
  // needs somewhere to go.
  poseTooltipUnsupplied: () =>
    'This position was saved with a tooltip that is no longer available.',
};

/**
 * `'auto'` needs a tooltip to read. A target that cannot supply one moves the
 * wrong frame, in one of two ways.
 *
 * Where the pose records no name at all — a pallet, a vision list, a pose taught
 * before the capture existed — `'auto'` resolves to nothing and the move lands
 * on the wrist flange, whatever is fitted.
 *
 * Where it records a name nothing offers, the name still reaches the offset
 * walk, and a device answers any `ee-*` option with its own transform: the arm
 * goes to whichever gripper is enabled, and only to the flange when none is.
 *
 * One function for the waypoint and the arc, which differ only in how many
 * targets they carry: either of an arc's two falling back bends the arc away
 * from what was taught, so both are judged and the strongest outcome reports —
 * not the first, which can be the weaker one.
 *
 * Which channel a reason takes turns on one question: can the operator fix it
 * without re-teaching the point? A reason that no fit, enable or calibration
 * can answer is thrown, which `actionRequiredToDiagnostic` stamps
 * `severity: 'error'`, and the step stops playing — waiting would only mean
 * running at the wrong frame indefinitely. A reason the robot can grow out of
 * is returned through `warn()` and stops nothing, the way the legacy
 * dangling-`tcpOption` warning does.
 *
 * `danglingTooltip` on the warning side rather than a missing-configuration
 * kind: the configuration is present and complete, and the tooltip it asks for
 * is the thing that cannot be answered.
 *
 * @throws {ActionRequiredError} for a target no equipment change can fix.
 */
export function reportSavedTooltipProblem({
  targets,
  spaceItems,
  equipment,
  stepNoun,
}: {
  targets: MotionTargetReference[];
  spaceItems: Space.Item[];
  /** This robot's equipment, for judging whether a taught tooltip is on it. */
  equipment: EquipmentItem[] | undefined;
  /** Names the step in the operator's sentence: "for this waypoint". */
  stepNoun: string;
}): WarningFinding | undefined {
  const reasons = targets
    .map((target) => noSavedTooltipReason({ ...target, spaceItems, equipment }))
    .filter((reason) => reason != null);

  // Every target is judged before any reports, because the tiers differ and the
  // stronger one has to win. An arc warning on its first target and refusing on
  // its second would otherwise play: the warning returns, and the refusal is
  // never reached. Among targets at the SAME tier the first still reports —
  // they stop the step identically, and naming one of them is enough.
  const refusing = reasons.find((reason) =>
    NO_EQUIPMENT_CHANGE_FIXES_IT.has(reason),
  );

  if (refusing != null) {
    throw new ActionRequiredError({
      kind: 'invalidConfiguration',
      message: NO_SAVED_TOOLTIP_MESSAGE[refusing](stepNoun),
      fieldId: 'tcpOption',
    });
  }

  const [warning] = reasons;

  if (warning == null) return undefined;

  return {
    kind: 'danglingTooltip',
    message: NO_SAVED_TOOLTIP_MESSAGE[warning](stepNoun),
    fieldId: 'tcpOption',
  };
}
